import React, { useEffect, useMemo, useRef, useState } from 'react';

import { useForm, useWatch } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { AppRoutes } from 'utilitys/appRoutes/AppRoutes';
import { getStringWithoutHash } from 'utilitys/helpers/general';

import useCustomExcelFileName from 'hooks/useCustomExcelFileName';
import useCustomPageSubTitle from 'hooks/useCustomPageSubTitle';
import { DEFAULT_ERROR, useCustomSnackBar } from 'hooks/useCustomSnackBar';
import useEventWithDimensions from 'hooks/useEventWithDimensions';
import { useGlobalFiltersHook } from 'hooks/useGlobalFiltersHook';
import useTableCurrentState from 'hooks/useTableCurrentState';

import { useConfigurationStore } from 'store/configurationsStore';

import { useMutationExportCsvReport } from 'api/mutations/exportCsvReport.mutation';
import { useMutationSetWidgetColumns } from 'api/mutations/setWidgetColumns.mutation';
import { useQueryVersionCompareTrackedActivitiesTableData } from 'api/queries/versionCompareTrackedActivitiesTableData.query';

import { ColumnsManager } from 'components/common/ColumnsManager/ColumnsManager';
import { ExportDropdown } from 'components/common/ExportDropdown/exportDropdown';
import { FilterNames, getFiltersData } from 'components/common/GlobalFiltersInPage/globalFiltersInPage.utils';
import NoData from 'components/common/NoData/noData';
import OverlayWithSpinner from 'components/common/OverlayWithSpinner/overlayWithSpinner';
import MainTable from 'components/common/Tables/MainTable/MainTable';
import { TooltipIcon } from 'components/common/TooltipIcon/tooltipIcon';
import { WidgetTitle } from 'components/common/WidgetTitle/WidgetTitle';
import { WidgetWithTitle } from 'components/common/WidgetWithTitle/WidgetWithTitle';

import { AddWidget } from 'components/Dashboards/Project/Components/CustomDashboard/AddRemoveWidget/addWidget';
import { PinboardWidgetPills } from 'components/Dashboards/Project/Components/CustomDashboard/PinboardWidgetPills/PinboardWidgetPills';
import { ITableWidgetWrapperProps } from 'components/Dashboards/Project/Components/CustomDashboard/utils/utils.type';
import { TableFooter } from 'components/Dashboards/Project/Components/VersionCompare/TableFotter/TableFooter';
import classes from 'components/Dashboards/Project/Components/VersionCompare/style/Table.module.scss';
import { TKeys, generateTabs } from 'components/Dashboards/Project/Components/VersionCompare/utils/helper';
import { smallPageSize } from 'components/Dashboards/Project/Components/VersionCompare/utils/table.utils';
import { executionConfig } from 'components/Dashboards/Project/ProjectWrapper/executionConfig';
import { ThreeDotsMenu } from 'components/Dashboards/Reporting/threeDotsMenu/ThreeDotsMenu';
import { Tabs } from 'components/DesignSystem/Tabs';
import { CriticalPathFilter } from 'components/widgets/ChangeAnalysisTrackedActivities/tableLocalFilters/filters/criticalPathFilter';
import { StatusFilter } from 'components/widgets/ChangeAnalysisTrackedActivities/tableLocalFilters/filters/statusFilter';
import {
    ITableLocalFiltersFields,
    TableLocalFilters,
} from 'components/widgets/ChangeAnalysisTrackedActivities/tableLocalFilters/tableLocalFilters';
import {
    columns,
    initialState,
} from 'components/widgets/ChangeAnalysisTrackedActivities/versionCompareTrackedActivities.utils';
import { statusOptions } from 'components/widgets/KeyActivities/components/filter.utils';

export const VersionCompareTrackedActivities = ({
    projectId,
    contractId,
    widgetId,
    latestVersionId,
    compareVersionId,
    filter,
    globalFilterData,
    externalComponents = null,
    editNarrative = null,
    setSubTitleForExport,
    localFilters,
}: ITableWidgetWrapperProps) => {
    const {
        routeId: id,
        name: configTitle,
        componentKey,
        endpoints,
    } = useConfigurationStore().myVersionCompareTrackedActivities;
    const title = configTitle;
    const endpoint = endpoints.getTrackedItems;
    const tableRef = useRef(null);
    const [activeTab, setActiveTab] = useState<TKeys>(
        widgetId && localFilters?.activeTab ? (localFilters?.activeTab as TKeys) : 'next',
    );

    const route = `${getStringWithoutHash(window.location.href)}#${id}`;
    const subTitle = useCustomPageSubTitle({ projectId, contractId, latestVersionId });

    const [spinnerOpen, setSpinnerOpen] = useState<boolean>(false);
    const mutation = useMutationExportCsvReport();
    const { handleSnackBar } = useCustomSnackBar();
    const updatedInitialState = useTableCurrentState({ initialState, componentKey, widgetId });
    const { mutate: mutateSetWidgetColumns } = useMutationSetWidgetColumns();

    const { sendEventWithDimensions } = useEventWithDimensions();

    const filters = useMemo(
        () =>
            widgetId
                ? filter?.filters
                : getFiltersData({
                      [FilterNames.ACTIVITY_CODES]: globalFilterData?.activityCodes
                          ?.filter((item) => !item.isExclude)
                          .map((i) => i.name),
                      [FilterNames.ACTIVITY_CODES_EXCLUDE]: globalFilterData?.activityCodes
                          ?.filter((item) => item.isExclude)
                          .map((i) => i.name),
                      [FilterNames.FRAGNET_SEARCH]: globalFilterData?.milestoneFragnet,
                      [FilterNames.WBS_SEARCH]: globalFilterData?.wbs,
                      [FilterNames.TAG_CARD_BOARD]: globalFilterData?.tags
                          ?.filter((item) => !item.isExclude)
                          .map((i) => i.name),
                      [FilterNames.TAG_CARD_BOARD_EXCLUDE]: globalFilterData?.tags
                          ?.filter((item) => item.isExclude)
                          .map((i) => i.name),
                  }),

        [filter, globalFilterData],
    );

    const { data, isFetching } = useQueryVersionCompareTrackedActivitiesTableData(
        {
            projectId: projectId,
            contractId: contractId,
            latestVersionId: latestVersionId,
            compareVersionId: compareVersionId,
            filter: {
                filters,
            },
        },
        endpoint,
    );

    const formattedLocalFilters = useMemo(() => {
        return widgetId && localFilters
            ? getFiltersData({
                  [FilterNames.OPTIONS_STATUS]: localFilters?.status?.map((item) => item.label) || [],
                  [FilterNames.CRITICAL_PATH_USER_CARD_BOARD]: localFilters?.criticalPath ? ['Yes'] : ['No'],
              })
            : [];
    }, [widgetId, localFilters]);

    const mergedFilters = useMemo(() => {
        return widgetId && filter?.filters
            ? {
                  filters: [...filter?.filters, ...formattedLocalFilters],
              }
            : undefined;
    }, [widgetId, filter, formattedLocalFilters]);

    const { generateFilters: formattedFilter } = useGlobalFiltersHook({
        widgetId,
        filter: mergedFilters,
        latestVersionId,
    });

    const reportType = data?.typeId || '';
    const fileName = useCustomExcelFileName({
        projectId,
        contractId,
        latestVersionId,
        title,
        prefix: 'Change_Analysis_',
    });
    const [pageSize, setPageSize] = useState(smallPageSize);

    const tabsArrData = generateTabs({ data });

    // ============================== Local Filters
    const { control, setValue, watch } = useForm<ITableLocalFiltersFields>({
        defaultValues: {
            status: statusOptions,
            criticalPath: false,
        },
    });

    const formValues = useWatch({ control });

    const activeData = useMemo(() => {
        if (data && data.periodItemsList) {
            const periodItemsList = data.periodItemsList;
            return periodItemsList.find((item) => item.id === activeTab)?.itemsList || periodItemsList[0].itemsList;
        }
        return [];
    }, [data, activeTab]);

    const filteredData = useMemo(() => {
        const status = widgetId ? localFilters?.status : formValues.status;
        const criticalPath = widgetId ? localFilters?.criticalPath : formValues.criticalPath;
        const statusArray = status?.map((item) => item.value);

        if (activeData) {
            return activeData
                .filter((item) => statusArray?.includes(item.latestVersion.cardBoardViewResponse.status.name))
                .filter((item) =>
                    criticalPath
                        ? item.latestVersion.cardBoardViewResponse.cardIndicators.isCriticalPath.isIndicatorOn ===
                          criticalPath
                        : item,
                );
        }
        return null;
    }, [activeData, localFilters, formValues]);
    // ============================== Local Filters

    const hideComponent = useMemo(() => {
        if (data && data.periodItemsList) {
            const periodItemsList = data.periodItemsList;
            const allData = periodItemsList.find((item) => item.id === 'all');
            return allData ? allData.itemsList.length === 0 : true;
        }
        return true;
    }, [data]);

    const [modalColumnsOpen, setModalColumnsOpen] = useState<boolean>(false);
    useEffect(() => {
        setSubTitleForExport && setSubTitleForExport({ widgetId, subTitle, title });
    }, [widgetId, subTitle, title]);

    if (hideComponent) {
        return null;
    }
    const handleAPIExcelExport = () => {
        setSpinnerOpen(true);
        mutation.mutate(
            {
                compareVersionId,
                contractId,
                filter: { filters },
                latestVersionId,
                period: activeTab,
                projectId,
                reportType,
                fileName,
            },
            {
                onSuccess: () => {
                    setSpinnerOpen(false);
                    handleSnackBar('File Successfully Downloaded', 'success');

                    sendEventWithDimensions({
                        category: 'Export',
                        action: 'Excel',
                        label: `${title} ${subTitle}`,
                    });
                },
                onError: (error) => {
                    setSpinnerOpen(false);
                    DEFAULT_ERROR({ error, handleError: handleSnackBar });
                },
            },
        );
    };

    const setWidgetColumns = (widgetId: string) => {
        if (tableRef.current) {
            const { hiddenColumns, columnOrder } = tableRef.current.state;
            mutateSetWidgetColumns(
                { columns: { hiddenColumns, columnOrder }, widgetId },
                {
                    onError: (error) => {
                        DEFAULT_ERROR({ error, handleError: handleSnackBar });
                    },
                },
            );
        }
    };

    return (
        <WidgetWithTitle
            title={<WidgetTitle title={title} />}
            id={id}
            titleFilters={[
                editNarrative && <div key={'editNarrative'}>{editNarrative}</div>,
                widgetId && formattedFilter.length > 0 ? (
                    <PinboardWidgetPills key={'widgetFilters'} filters={formattedFilter} />
                ) : (
                    <TableLocalFilters key={'filters'}>
                        <StatusFilter control={control} />
                        <CriticalPathFilter value={watch('criticalPath')} setValue={setValue} />
                    </TableLocalFilters>
                ),
            ]}
            titleComponents={[
                externalComponents && <div key={'externalComponents'}>{externalComponents}</div>,
                <AddWidget
                    key={'AddWidget'}
                    componentKey={componentKey}
                    title={title}
                    projectId={projectId}
                    contractId={contractId}
                    elementId={id}
                    widgetId={widgetId}
                    route={route}
                    filters={filters}
                    localFilters={{ ...formValues, activeTab: activeTab }}
                    callBack={setWidgetColumns}
                    shouldHide={true}
                />,
                <ExportDropdown
                    title={title}
                    key={'export'}
                    subTitle={subTitle}
                    handleAPIExcelExport={handleAPIExcelExport}
                />,
                tableRef?.current && (
                    <ColumnsManager
                        onOpenModal={() => setModalColumnsOpen(true)}
                        onHideModal={() => setModalColumnsOpen(false)}
                        showModal={modalColumnsOpen}
                        widgetId={widgetId}
                        componentKey={componentKey}
                        tableInstance={tableRef.current}
                        initialState={initialState}
                        columns={columns}
                    />
                ),
                <TooltipIcon key={'info'} tooltip={data?.info} />,
                <ThreeDotsMenu widgetId={widgetId} componentKey={componentKey} shareTitle={title} shareLink={route} />,
            ]}
        >
            {filteredData && (
                <div className={classes.tableWrapperContainer}>
                    {tabsArrData.length > 0 && (
                        <Tabs tabs={tabsArrData} value={activeTab} onChange={(value) => setActiveTab(value as TKeys)} />
                    )}
                    {updatedInitialState && (
                        <MainTable
                            getInstanceCallback={(instance) => (tableRef.current = instance)}
                            data={filteredData}
                            columns={columns}
                            initialState={updatedInitialState}
                            pageSize={pageSize}
                            noData={
                                <NoData>
                                    No tracked activities added yet. Find and add via{' '}
                                    <Link
                                        to={AppRoutes.buildPath(
                                            AppRoutes.DASHBOARD_EXECUTION_PATH,
                                            executionConfig.battlecards.link,
                                        )}
                                    >
                                        {executionConfig.battlecards.title}
                                    </Link>
                                </NoData>
                            }
                            isLoading={isFetching}
                        />
                    )}
                    {filteredData.length > smallPageSize && (
                        <TableFooter setPageSize={setPageSize} pageSize={pageSize} />
                    )}
                </div>
            )}
            {spinnerOpen && <OverlayWithSpinner />}
        </WidgetWithTitle>
    );
};
